/* eslint-disable no-underscore-dangle */
import {
  createApi, fetchBaseQuery,
} from '@reduxjs/toolkit/query/react';
import { METCONSOLE_API } from '../../configs/endpoints';
import { HistoryDto, IOPointsDto, TokenDto } from './dto';
import { METCONSOLE_FIWARE_SERVICE } from '../../configs/credentials';
import { IOPointRoute } from '../../types/metconsole.types';
import { utcDayjs } from '../../utils';
import { connectionTimeout } from '../../configs/initialValues';

const baseQuery = fetchBaseQuery({
  baseUrl: METCONSOLE_API,
  timeout: connectionTimeout,
  prepareHeaders: (headers) => {
    headers.set('Fiware-Service', METCONSOLE_FIWARE_SERVICE);
    if (!((window as any)._env_.REACT_APP_DEVELOPMENT_MODE === 'true')) {
      headers.set('Authorization', METCONSOLE_FIWARE_SERVICE);
    }
    return headers;
  },
});

const api = createApi({
  reducerPath: 'meconsole-api',
  baseQuery,
  endpoints: (builder) => ({
    getLoggedInMetConsole: builder.mutation<TokenDto, {email: string, password: string}>({
      query: ({ email, password }) => ({
        url: 'user/login',
        method: 'POST',
        body: { email, password },
      }),
    }),
    getIOPoint: builder.query<unknown, IOPointRoute>({
      queryFn: async ({
        service, group, station, variable,
      }, baseQueryApi, extraOptions, baseQuery) => {
        const url = `iopoint?station=${station}&group=${group}&variable=${variable}&flat=true`;
        const iopoints = await baseQuery({
          url,
        });

        if (iopoints?.error) {
          return iopoints;
        }

        const IOPointsResponse = iopoints.data as IOPointsDto;

        return { data: IOPointsResponse.iopoints[0]?.value };
      },
    }),
    getHistoricalRangeIOPoints: builder.query<Record<string, unknown>,
    {route: IOPointRoute, range: string[]}>({
      queryFn: async ({
        route: {
          group, station, variable,
        }, range,
      }, baseQueryApi, extraOptions, baseQuery) => {
        const limit = 100;

        const fetchAllPages = async (
          page: number = 0,
          accumulated: HistoryDto = [],
        ): Promise<any> => {
          const url = `analytics/iopoints?station=${station}&group=${group}&variable=${variable}&after=${range[0]}&until=${range[1]}&page=${page}&limit=${limit}`;
          const iopoints = await baseQuery({ url });

          if (iopoints?.error) {
            return iopoints;
          }

          const history = iopoints.data as HistoryDto;
          const allHistory = accumulated.concat(history);

          if (history.length < limit) {
            return {
              data: allHistory.reduce<Record<string, unknown>>((acc, cur) => {
                acc[utcDayjs(cur.time).toISOString()] = (typeof cur.value === 'object' && cur.value !== null && Object.keys(cur.value).length === 0) ? '' : cur.value;
                return acc;
              }, {}),
            };
          }

          return fetchAllPages(page + 1, allHistory);
        };

        return fetchAllPages();
      },
    }),
  }),
});

export const {
  useGetLoggedInMetConsoleMutation,
  useGetIOPointQuery,
  useLazyGetIOPointQuery,
  useGetHistoricalRangeIOPointsQuery,
  useLazyGetHistoricalRangeIOPointsQuery,
} = api;

export default api;
